import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '../config/supabase';
import { getCoachVisibleEmail, getCoachVisibleProfileNumber } from './useCoachAthleteSummary';
import { useCoachPreferences } from './useCoachPreferences';
import { isHiddenFromCoachRosterAfterConsentWithdraw } from '../utils/rosterConsentGate';

function coachVisibleRosterMetrics(rower: {
  height_cm?: number | null;
  weight_kg?: number | null;
  erg_2k_seconds?: number | string | null;
  email?: string | null;
  profile_visibility?: unknown;
}) {
  const vis = rower.profile_visibility;
  return {
    heightCm: getCoachVisibleProfileNumber(rower.height_cm, vis, 'height_cm'),
    weightKg: getCoachVisibleProfileNumber(rower.weight_kg, vis, 'weight_kg'),
    erg2kSeconds: getCoachVisibleProfileNumber(rower.erg_2k_seconds, vis, 'erg_2k_seconds'),
    email: getCoachVisibleEmail(rower.email, vis),
  };
}

export interface CoachRosterRower {
  profileId: string;
  teamId: string | null; // primary team when deduped; null for legacy
  teamIds?: string[]; // all team ids when deduped
  teamName?: string | null;
  teamNames?: string[]; // all team names when deduped
  division?: string | null;
  firstName: string;
  lastName: string;
  preferredName?: string | null;
  alias?: string | null;
  isDemo?: boolean;
  accountStatus?: string | null;
  sessionsParticipated?: number; // distinct sessions (from telemetry_rowers) when deduped
  /** From rower_profiles; may be null if not set */
  heightCm?: number | null;
  weightKg?: number | null;
  erg2kSeconds?: number | null;
  email?: string | null;
  /** From rower_profiles.date_of_birth; used only to gate the parent-consent badge/nav block for minors. */
  dateOfBirth?: string | null;
  /** team_roster_entries.consent_given_at for the primary team row; null means coaches can't map this rower yet (pending invite acceptance or a parent withdrew consent). */
  consentGivenAt?: string | null;
  /** team_roster_entries.consent_withdrawn_at for the primary team row; set when a parent/guardian withdrew consent. */
  consentWithdrawnAt?: string | null;
}

export interface UseCoachRosterResult {
  roster: CoachRosterRower[];
  loading: boolean;
  error: Error | null;
  refresh: () => Promise<void>;
}

export function useCoachRoster(teamId?: string | null): UseCoachRosterResult {
  const [roster, setRoster] = useState<CoachRosterRower[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const { preferences: coachPreferences } = useCoachPreferences();

  const fetchRoster = useCallback(async () => {
    setLoading(true);
    setError(null);

    type TeamRosterEntryResponse = {
      team_id: string;
      alias: string | null;
      consent_given_at: string | null;
      consent_withdrawn_at: string | null;
      rower: {
        id: string;
        first_name: string;
        last_name: string;
        preferred_name: string | null;
        account_status: string | null;
        is_demo?: boolean;
        date_of_birth?: string | null;
        height_cm?: number | null;
        weight_kg?: number | null;
        erg_2k_seconds?: number | string | null;
        email?: string | null;
        profile_visibility?: unknown;
      } | null;
      team?: {
        id: string;
        is_demo?: boolean;
        name?: string | null;
        division?: string | null;
      };
    };

    // Fetch team roster entries
    let query = supabase.from('team_roster_entries').select(
      `team_id,
         alias,
         consent_given_at,
         consent_withdrawn_at,
         rower:rower_profiles(id, first_name, last_name, preferred_name, account_status, date_of_birth, height_cm, weight_kg, erg_2k_seconds, email, profile_visibility),
         team:teams(id, is_demo, name, division)`
    );

    // Filter by teamId if provided
    if (teamId) {
      query = query.eq('team_id', teamId);
    }

    const { data: teamData, error: teamError } = await query
      .returns<TeamRosterEntryResponse[]>()
      .order('created_at', { ascending: true });

    if (teamError) {
      setError(teamError || new Error('Failed to fetch roster'));
      setRoster([]);
      setLoading(false);
      return;
    }

    let normalized: CoachRosterRower[];

    // If teamId is provided, just return that team's rowers directly
    // Otherwise, separate user's team rowers from demo team rowers
    if (teamId) {
      // For a specific team, just normalize all rowers from that team
      normalized = (teamData || [])
        .map((entry) => {
          const rower = entry.rower;
          if (!rower) return null;
          const team = entry.team as
            | {
                id: string;
                is_demo?: boolean;
                name?: string | null;
                division?: string | null;
              }
            | undefined;
          return {
            profileId: rower.id,
            teamId: entry.team_id,
            teamName: team?.name ?? null,
            division: team?.division ?? null,
            firstName: rower.first_name || '',
            lastName: rower.last_name || '',
            preferredName: rower.preferred_name,
            alias: entry.alias ?? null,
            isDemo: team?.is_demo === true,
            accountStatus: rower.account_status,
            dateOfBirth: rower.date_of_birth ?? null,
            consentGivenAt: entry.consent_given_at ?? null,
            consentWithdrawnAt: entry.consent_withdrawn_at ?? null,
            ...coachVisibleRosterMetrics(rower),
          } as CoachRosterRower;
        })
        .filter(Boolean) as CoachRosterRower[];

      // Fetch team-specific session counts
      try {
        const { data: teamSessions } = await supabase
          .from('telemetry_sessions')
          .select('id')
          .eq('team_id', teamId);
        const sessionIds = (teamSessions || []).map((s) => s.id).filter(Boolean);
        if (sessionIds.length > 0) {
          const { data: tr } = await supabase
            .from('telemetry_rowers')
            .select('session_id, roster_id')
            .in('session_id', sessionIds);
          const countByRosterId = new Map<string, Set<string>>();
          type TelemetryRowerRow = { session_id?: unknown; roster_id?: unknown };
          for (const row of (tr || []) as TelemetryRowerRow[]) {
            const sid = row.session_id as string;
            if (row.roster_id) {
              const rid = row.roster_id as string;
              const set = countByRosterId.get(rid) || new Set<string>();
              set.add(sid);
              countByRosterId.set(rid, set);
            }
          }
          normalized = normalized.map((r) => {
            const byProfileCount = countByRosterId.get(r.profileId)?.size;
            const sessionsParticipated = (byProfileCount ?? 0) as number;
            return { ...r, sessionsParticipated };
          });
        }
      } catch {
        // leave sessionsParticipated undefined on error
      }
    } else {
      // For global roster (no teamId), separate user's team rowers from demo team rowers
      const userTeamRowers: TeamRosterEntryResponse[] = [];
      const demoTeamRowers: TeamRosterEntryResponse[] = [];

      (teamData || []).forEach((entry) => {
        const team = entry.team as
          | {
              id: string;
              is_demo?: boolean;
              name?: string | null;
              division?: string | null;
            }
          | undefined;
        if (team?.is_demo === true) {
          demoTeamRowers.push(entry);
        } else {
          userTeamRowers.push(entry);
        }
      });

      // Normalize team roster entries (user's teams only)
      const teamRowers = userTeamRowers
        .map((entry) => {
          const rower = entry.rower;
          if (!rower) return null;
          const team = entry.team as
            | {
                id: string;
                name?: string | null;
                division?: string | null;
              }
            | undefined;
          return {
            profileId: rower.id,
            teamId: entry.team_id,
            teamName: team?.name ?? null,
            division: team?.division ?? null,
            firstName: rower.first_name || '',
            lastName: rower.last_name || '',
            preferredName: rower.preferred_name,
            alias: entry.alias ?? null,
            isDemo: false,
            accountStatus: rower.account_status,
            dateOfBirth: rower.date_of_birth ?? null,
            consentGivenAt: entry.consent_given_at ?? null,
            consentWithdrawnAt: entry.consent_withdrawn_at ?? null,
            ...coachVisibleRosterMetrics(rower),
          } as CoachRosterRower;
        })
        .filter(Boolean) as CoachRosterRower[];

      // Normalize demo rowers (if preference allows)
      const demoRowers: CoachRosterRower[] = coachPreferences.showDemoContent
        ? (demoTeamRowers
            .map((entry) => {
              const rower = entry.rower;
              if (!rower) return null;
              const team = entry.team as
                | {
                    id: string;
                    name?: string | null;
                    division?: string | null;
                  }
                | undefined;
              return {
                profileId: rower.id,
                teamId: entry.team_id,
                teamName: team?.name ?? null,
                division: team?.division ?? null,
                firstName: rower.first_name || '',
                lastName: rower.last_name || '',
                preferredName: rower.preferred_name,
                alias: entry.alias ?? null,
                isDemo: true,
                accountStatus: rower.account_status,
                dateOfBirth: rower.date_of_birth ?? null,
                consentGivenAt: entry.consent_given_at ?? null,
                consentWithdrawnAt: entry.consent_withdrawn_at ?? null,
                ...coachVisibleRosterMetrics(rower),
              } as CoachRosterRower;
            })
            .filter(Boolean) as CoachRosterRower[])
        : [];

      // Dedupe by profileId (one row per athlete)
      const byProfile = new Map<string, CoachRosterRower>();
      for (const r of [...teamRowers, ...demoRowers]) {
        const existing = byProfile.get(r.profileId);
        if (!existing) {
          byProfile.set(r.profileId, {
            ...r,
            teamIds: r.teamId ? [r.teamId] : [],
            teamNames: r.teamName ? [r.teamName] : [],
          });
        } else {
          const ids = new Set(existing.teamIds || []);
          const names = new Set(existing.teamNames || []);
          if (r.teamId) ids.add(r.teamId);
          if (r.teamName) names.add(r.teamName);
          existing.teamIds = Array.from(ids);
          existing.teamNames = Array.from(names);
          existing.division = existing.division || r.division;
          existing.heightCm = existing.heightCm ?? r.heightCm;
          existing.weightKg = existing.weightKg ?? r.weightKg;
          existing.erg2kSeconds = existing.erg2kSeconds ?? r.erg2kSeconds;
          existing.email = existing.email ?? r.email;
          existing.dateOfBirth = existing.dateOfBirth ?? r.dateOfBirth;
          // Consent still needed if it's missing on any of the athlete's team rows.
          existing.consentGivenAt = existing.consentGivenAt ?? r.consentGivenAt;
          existing.consentWithdrawnAt = existing.consentWithdrawnAt ?? r.consentWithdrawnAt;
        }
      }
      normalized = Array.from(byProfile.values());

      // Fetch session counts for coach's sessions (RLS restricts to current user)
      try {
        const { data: sessions } = await supabase
          .from('telemetry_sessions')
          .select('id')
          .not('id', 'is', null);
        const sessionIds = (sessions || []).map((s) => s.id).filter(Boolean);
        if (sessionIds.length > 0) {
          const { data: tr } = await supabase
            .from('telemetry_rowers')
            .select('session_id, roster_id')
            .in('session_id', sessionIds);
          const countByRosterId = new Map<string, Set<string>>();
          type TelemetryRowerRow = { session_id?: unknown; roster_id?: unknown };
          for (const row of (tr || []) as TelemetryRowerRow[]) {
            const sid = row.session_id as string;
            if (row.roster_id) {
              const rid = row.roster_id as string;
              const set = countByRosterId.get(rid) || new Set<string>();
              set.add(sid);
              countByRosterId.set(rid, set);
            }
          }
          normalized = normalized.map((r) => {
            const byProfileCount = countByRosterId.get(r.profileId)?.size;
            const sessionsParticipated = (byProfileCount ?? 0) as number;
            return { ...r, sessionsParticipated };
          });
        }
      } catch {
        // leave sessionsParticipated undefined on error
      }
    }

    // GLY-363: once a parent's withdrawal has aged past the grace period, drop the rower
    // from the roster entirely rather than just badging/blocking them (see rosterConsentGate).
    normalized = normalized.filter(
      (rower) =>
        !isHiddenFromCoachRosterAfterConsentWithdraw({
          consentWithdrawnAt: rower.consentWithdrawnAt,
          dateOfBirth: rower.dateOfBirth,
        })
    );

    setRoster(normalized);
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coachPreferences.showDemoContent, teamId]);

  useEffect(() => {
    fetchRoster();
  }, [fetchRoster]);

  // Filter roster based on showDemoContent preference
  const filteredRoster = useMemo(() => {
    if (coachPreferences.showDemoContent) return roster;
    return roster.filter((rower) => !rower.isDemo);
  }, [roster, coachPreferences.showDemoContent]);

  return useMemo(
    () => ({ roster: filteredRoster, loading, error, refresh: fetchRoster }),
    [filteredRoster, loading, error, fetchRoster]
  );
}
